// src/components/Admin/MassVendorUpload.tsx
import React, {
  useState, useEffect, useRef, useMemo, useCallback
} from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../lib/firebase';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { supabase } from '../../lib/supabaseClient';
import {
  ArrowLeft, Upload, ClipboardPaste, Table2, ZoomIn, ZoomOut, Maximize2,
  CheckCircle, XCircle, AlertCircle, Loader, RefreshCw, Store, Trash2, Info,
  Wand2, Move, GripVertical
} from 'lucide-react';
import './MassVendorUpload.css';

// ============================================================
// TYPES
// ============================================================
type CellKey =
  | 'name' | 'phone' | 'location' | 'bio' | 'email'
  | 'website' | 'instagram' | 'facebook' | 'twitter'
  | 'vendcat' | 'categoryHint' | 'hashtags';

interface CleanRow {
  name: string;
  phone: string;
  phone2: string;
  location: string;
  bio: string;
  email: string;
  website: string;
  instagram: string;
  facebook: string;
  twitter: string;
  hashtags: string;
  vendcat: number | null;
  categoryHint: string;
  warnings: string[];
  extras: string[];
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface CategoryRow { id: number; name: string; }

// ============================================================
// NIGERIAN PHONE INTELLIGENCE
// ============================================================
// A valid Nigerian MSISDN: 11 digits, starts with 0 followed by 7/8/9.
function normalizeNigerianPhone(raw: string): string | null {
  if (!raw) return null;
  let d = raw.replace(/[^\d+]/g, '');

  // Country-code forms
  if (/^\+234\d{9,10}$/.test(d)) d = '0' + d.slice(4);
  else if (/^234\d{9,10}$/.test(d)) d = '0' + d.slice(3);

  d = d.replace(/\D/g, '');

  // 10 digits starting 7/8/9 → prepend 0
  if (d.length === 10 && /^[789]/.test(d)) d = '0' + d;

  // 11 digits starting 0 → validate
  if (d.length === 11 && /^0[789]\d{9}$/.test(d)) return d;

  // 11 digits starting 7/8/9 (missing leading 0) → prepend 0, keep 10-digit body
  if (d.length === 11 && /^[789]/.test(d)) {
    const candidate = '0' + d.slice(0, 10);
    if (/^0[789]\d{8}$/.test(candidate)) return candidate;
  }

  // Final check
  if (/^0[789]\d{9}$/.test(d)) return d;

  return null;
}

function extractNigerianPhones(text: string): string[] {
  if (!text) return [];
  const found = new Set<string>();

  // Split on separators (preserve spaces so "0803 839 3389" stays whole)
  const candidates = text.split(/[\/,;|•\n\t]+/);
  candidates.forEach(c => {
    const n = normalizeNigerianPhone(c);
    if (n) found.add(n);
  });

  // Fallback: scan any digit run in the whole string
  if (found.size === 0) {
    const runs = text.match(/(\+?234|0)?[\d\s\-]{9,15}\d/g) || [];
    runs.forEach(r => {
      const n = normalizeNigerianPhone(r);
      if (n) found.add(n);
    });
  }

  return Array.from(found);
}

const looksLikePhone = (s: string): boolean => {
  const t = s.trim();
  if (!t) return false;
  if (extractNigerianPhones(t).length > 0) return true;
  const d = t.replace(/\D/g, '');
  if (d.length < 7 || d.length > 15) return false;
  if (/^\d{1,3}$/.test(t)) return false;
  return /^[+]?\d[\d\s\-()/]{5,}\d$/.test(t);
};

// ============================================================
// OTHER CLASSIFIERS
// ============================================================
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const looksLikeEmail = (s: string) => RE_EMAIL.test(s.trim());

const looksLikeUrl = (s: string): boolean => {
  const t = s.trim();
  if (!t) return false;
  if (/^(https?:\/\/|www\.)/i.test(t)) return true;
  if (/^@[\w.]+$/.test(t)) return false;
  return /\b[a-z0-9-]+\.(com|ng|net|org|io|co|app|site|xyz|me|ly|gg)\b/i.test(t);
};

const isHandle = (s: string) => /^@[\w.]+$/.test(s.trim());

const splitTokens = (s: string): string[] =>
  s.split(/[\s,;|•]+/).map(t => t.trim()).filter(Boolean);

const looksLikeHashtags = (s: string): boolean => {
  const t = s.trim();
  if (!t) return false;
  const tokens = splitTokens(t);
  if (tokens.length === 0) return false;
  if (tokens.length === 1) return tokens[0].startsWith('#');
  return tokens.every(t => t.startsWith('#'));
};

const hasHashtagRun = (s: string): string[] => {
  return splitTokens(s).filter(t => t.startsWith('#'));
};

const looksLikeName = (s: string): boolean => {
  const t = s.trim();
  if (!t) return false;
  if (looksLikePhone(t) || looksLikeEmail(t) || looksLikeUrl(t) || looksLikeHashtags(t) || isHandle(t)) return false;
  return /[a-zA-Z]/.test(t) && t.length <= 120;
};

const looksLikeLocation = (s: string): boolean => {
  const t = s.trim();
  if (!t) return false;
  if (looksLikePhone(t) || looksLikeEmail(t) || looksLikeUrl(t) || looksLikeHashtags(t)) return false;
  return (
    t.includes(',') ||
    /\b(state|road|street|avenue|close|estate|lga|junction|expressway|awka|lagos|abuja|enugu|anambra|nibo|okpuno|ifite|aroma|nsukka)\b/i.test(t)
  ) && t.length <= 400;
};

type SocialKind = 'instagram' | 'facebook' | 'twitter' | 'tiktok' | 'linkedin' | 'youtube' | 'other';
const detectSocial = (raw: string): SocialKind => {
  const u = raw.toLowerCase();
  if (u.includes('instagram.com') || isHandle(raw)) return 'instagram';
  if (u.includes('facebook.com') || u.includes('fb.com') || u.includes('fb.me')) return 'facebook';
  if (u.includes('twitter.com') || u.includes('x.com')) return 'twitter';
  if (u.includes('tiktok.com')) return 'tiktok';
  if (u.includes('linkedin.com')) return 'linkedin';
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  return 'other';
};

// ============================================================
// BLOCK SPLITTER
// ============================================================
function splitBlockIntoRawCells(block: string): string[] {
  const rejoined = block.replace(/([a-z0-9./_-])\n([a-z0-9./_-])/gi, '$1$2');
  const cells = rejoined.split(/\t+|\n+| {2,}/).map(c => c.trim()).filter(Boolean);
  const merged: string[] = [];
  let buf: string[] = [];
  const flush = () => { if (buf.length) { merged.push(buf.join(' ')); buf = []; } };
  cells.forEach(c => {
    const tokens = splitTokens(c);
    const allHash = tokens.length > 0 && tokens.every(t => t.startsWith('#'));
    if (allHash) buf.push(c);
    else { flush(); merged.push(c); }
  });
  flush();
  return merged;
}

// ============================================================
// MAIN PARSE
// ============================================================
interface ParseResult { rows: CleanRow[]; warnings: string[]; error?: string; }

function parseWithMarkers(text: string): ParseResult {
  const warnings: string[] = [];
  if (!text.trim()) return { rows: [], warnings };

  const raw = text.replace(/\r\n/g, '\n');
  const blockRegex = /@start([\s\S]*?)@end/gi;
  const blocks: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = blockRegex.exec(raw)) !== null) blocks.push(m[1]);

  if (blocks.length === 0) {
    return {
      rows: [], warnings: [],
      error: 'No rows found. Wrap each row in @start … @end.',
    };
  }

  const rows: CleanRow[] = [];

  blocks.forEach((block, blockIdx) => {
    const rawCells = splitBlockIntoRawCells(block);

    // Drop leading s/n integer
    if (rawCells.length >= 2 && /^\d+$/.test(rawCells[0]) && looksLikeName(rawCells[1])) {
      rawCells.shift();
    }

    const phoneCells: string[] = [];
    const emails: string[] = [];
    const urls: { value: string; kind: SocialKind }[] = [];
    const hashtagCells: string[] = [];
    const plain: string[] = [];
    const ints: number[] = [];

    rawCells.forEach(cell => {
      const c = cell.trim();
      if (!c) return;
      if (looksLikeHashtags(c)) { hashtagCells.push(c); return; }
      if (looksLikeEmail(c)) { emails.push(c); return; }

      // Phone BEFORE URL so "0803.../0818..." isn't mistaken for a URL.
      if (looksLikePhone(c)) {
        const nums = extractNigerianPhones(c);
        if (nums.length) {
          nums.forEach(n => { if (!phoneCells.includes(n)) phoneCells.push(n); });
          return;
        }
        phoneCells.push(c);
        return;
      }

      if (looksLikeUrl(c) || isHandle(c)) { urls.push({ value: c, kind: detectSocial(c) }); return; }
      if (/^\d{1,3}$/.test(c)) { ints.push(parseInt(c, 10)); return; }
      plain.push(c);
    });

    // Name — first plain that looks like a name
    let name = '';
    const plainRest: string[] = [];
    for (const p of plain) {
      if (!name && looksLikeName(p)) { name = p; continue; }
      plainRest.push(p);
    }

    // Fallback: if no phone yet, scan remaining plain text for one
    if (phoneCells.length === 0) {
      for (let i = 0; i < plainRest.length; i++) {
        const nums = extractNigerianPhones(plainRest[i]);
        if (nums.length) {
          nums.forEach(n => { if (!phoneCells.includes(n)) phoneCells.push(n); });
          plainRest.splice(i, 1);
          i--;
        }
      }
    }

    // Location
    let location = '';
    const plainLeft: string[] = [];
    for (const p of plainRest) {
      if (!location && looksLikeLocation(p)) { location = p; continue; }
      plainLeft.push(p);
    }
    if (!location) {
      const i = plainLeft.findIndex(p => p.includes(',') || p.length > 40);
      if (i >= 0) { location = plainLeft[i]; plainLeft.splice(i, 1); }
    }

    // Category hint
    let categoryHint = '';
    const plainFinal: string[] = [];
    plainLeft.forEach(p => {
      if (!categoryHint && /\b(nutrition|accommodation|fashion|electronics|beauty|services|education|agriculture|food|housing)\b/i.test(p) && p.length <= 80) {
        categoryHint = p;
      } else {
        plainFinal.push(p);
      }
    });

    // URL routing
    let instagram = '', facebook = '', twitter = '', website = '';
    const urlExtras: string[] = [];
    for (const u of urls) {
      if (u.kind === 'instagram' && !instagram) { instagram = u.value; continue; }
      if (u.kind === 'facebook' && !facebook) { facebook = u.value; continue; }
      if (u.kind === 'twitter' && !twitter) { twitter = u.value; continue; }
      if (u.kind === 'other' && !website) { website = u.value; continue; }
      if (!instagram) { instagram = u.value; continue; }
      if (!facebook) { facebook = u.value; continue; }
      if (!twitter) { twitter = u.value; continue; }
      if (!website) { website = u.value; continue; }
      urlExtras.push(u.value);
    }

    // Phones
    const phone = phoneCells[0] || '';
    const phone2 = phoneCells[1] || '';
    if (phoneCells.length > 2) warnings.push(`Row ${blockIdx + 1}: ${phoneCells.length} phones — first two kept.`);

    // Email
    const email = emails[0] || '';
    if (emails.length > 1) warnings.push(`Row ${blockIdx + 1}: ${emails.length} emails — first kept.`);

    // Hashtags
    const hashtagsStr = hashtagCells.join(' ');

    // vendcat
    let vendcat: number | null = null;
    if (ints.length) vendcat = ints[0];

    // Bio
    const bioParts: string[] = [];
    if (phone2) bioParts.push(`Alt: ${phone2}`);
    plainFinal.forEach(t => bioParts.push(t));
    urlExtras.forEach(u => bioParts.push(u));
    const bio = bioParts.join(' • ');

    const extras: string[] = [];
    if (ints.length > 1) extras.push(...ints.slice(1).map(String));

    rows.push({
      name, phone, phone2, location, bio, email,
      website, instagram, facebook, twitter,
      hashtags: hashtagsStr, vendcat, categoryHint,
      warnings: [], extras, status: 'pending',
    });
  });

  // Self-healing pass
  rows.forEach((row, idx) => {
    const pulled = rescueRow(row);
    if (pulled.length) warnings.push(`Row ${idx + 1}: recovered ${pulled.join(', ')} from bio/extras.`);
  });

  return { rows, warnings };
}

// ============================================================
// SELF-HEALING
// ============================================================
function rescueRow(row: CleanRow): string[] {
  const moved: string[] = [];
  const pool: string[] = [...splitBio(row.bio), ...row.extras];

  const take = (predicate: (s: string) => boolean): string | null => {
    for (let i = 0; i < pool.length; i++) {
      if (predicate(pool[i])) { const v = pool[i]; pool.splice(i, 1); return v; }
    }
    return null;
  };

  if (!row.hashtags) {
    const frags = pool.filter(f => looksLikeHashtags(f) || hasHashtagRun(f).length > 0);
    if (frags.length) {
      const collected: string[] = [];
      for (const f of frags) {
        collected.push(...(looksLikeHashtags(f) ? splitTokens(f) : hasHashtagRun(f)));
        const i = pool.indexOf(f);
        if (i >= 0) pool.splice(i, 1);
      }
      if (collected.length) { row.hashtags = collected.join(' '); moved.push('hashtags'); }
    }
  }

  if (!row.email) { const e = take(looksLikeEmail); if (e) { row.email = e; moved.push('email'); } }

  if (!row.phone) {
    const p = take(looksLikePhone);
    if (p) {
      const nums = extractNigerianPhones(p);
      if (nums.length) { row.phone = nums[0]; moved.push('phone'); }
      else { row.phone = p; moved.push('phone'); }
    }
  }
  if (!row.phone2) {
    const p = take(looksLikePhone);
    if (p) {
      const nums = extractNigerianPhones(p);
      const cand = nums.find(n => n !== row.phone);
      if (cand) { row.phone2 = cand; moved.push('phone2'); }
    }
  }

  if (!row.website) { const u = take(f => looksLikeUrl(f) && detectSocial(f) === 'other'); if (u) { row.website = u; moved.push('website'); } }
  if (!row.instagram) { const u = take(f => detectSocial(f) === 'instagram'); if (u) { row.instagram = u; moved.push('instagram'); } }
  if (!row.facebook) { const u = take(f => detectSocial(f) === 'facebook'); if (u) { row.facebook = u; moved.push('facebook'); } }
  if (!row.twitter) { const u = take(f => detectSocial(f) === 'twitter'); if (u) { row.twitter = u; moved.push('twitter'); } }
  if (!row.website) { const u = take(looksLikeUrl); if (u) { row.website = u; moved.push('website'); } }

  if (!row.location) { const l = take(looksLikeLocation); if (l) { row.location = l; moved.push('location'); } }

  row.bio = pool.join(' • ');
  return moved;
}

function splitBio(bio: string): string[] {
  if (!bio) return [];
  const parts = bio.split(/\s*•\s*/).map(s => s.trim()).filter(Boolean);
  const out: string[] = [];
  for (const part of parts) {
    const altMatch = part.match(/^Alt:\s*(.+)$/i);
    if (altMatch) { out.push(altMatch[1].trim()); continue; }
    const hashRun = hasHashtagRun(part);
    if (hashRun.length >= 1 && hashRun.length === splitTokens(part).length) {
      out.push(hashRun.join(' ')); continue;
    }
    out.push(part);
  }
  return out;
}

// ============================================================
// HASHTAGS
// ============================================================
const MAX_HASHTAGS = 5;

function normalizeHashtags(raw: string): string[] {
  return raw.split(/[\s,]+/).map(t => t.replace(/^#+/, '').trim()).filter(Boolean);
}

function splitHashtags(raw: string): { kept: string[]; overflow: string[] } {
  const all = normalizeHashtags(raw);
  return { kept: all.slice(0, MAX_HASHTAGS), overflow: all.slice(MAX_HASHTAGS) };
}

// ============================================================
// CATEGORY
// ============================================================
const FALLBACK_CATEGORY_MAP: Record<string, number> = {
  'food & nutrition': 1, 'food and nutrition': 1,
  'fashion & clothing': 2, 'electronics': 3,
  'health & beauty': 4, 'services': 5,
  'housing & accommodation': 9, 'housing and accommodation': 9,
  'education': 7, 'agriculture': 8,
};

function suggestVendcat(row: CleanRow, categories: CategoryRow[]): number | null {
  if (row.vendcat && row.vendcat > 0) return row.vendcat;
  if (!row.categoryHint) return null;
  const norm = (s: string) => s.trim().toLowerCase();
  const exact = categories.find(c => norm(c.name) === norm(row.categoryHint));
  if (exact) return exact.id;
  const fuzzy = categories.find(c => norm(c.name).includes(norm(row.categoryHint)) || norm(row.categoryHint).includes(norm(c.name)));
  if (fuzzy) return fuzzy.id;
  const key = norm(row.categoryHint);
  if (FALLBACK_CATEGORY_MAP[key] !== undefined) return FALLBACK_CATEGORY_MAP[key];
  return null;
}

function effectiveVendcat(row: CleanRow, categories: CategoryRow[]): { id: number | null; auto: boolean } {
  if (row.vendcat && row.vendcat > 0) return { id: row.vendcat, auto: false };
  const suggestion = suggestVendcat(row, categories);
  return { id: suggestion, auto: suggestion !== null };
}

// ============================================================
// MOVE
// ============================================================
function moveCellValue(row: CleanRow, from: CellKey, to: CellKey): CleanRow {
  if (from === to) return row;
  const srcVal = (row as any)[from];
  const dstVal = (row as any)[to];
  if (srcVal === '' || srcVal === null) return row;
  const next: CleanRow = { ...row };

  if (to === 'vendcat') {
    const n = parseInt(String(srcVal), 10);
    (next as any)[to] = isNaN(n) ? null : n;
  } else {
    (next as any)[to] = String(srcVal);
  }

  if (dstVal !== '' && dstVal !== null) {
    if (from === 'vendcat') {
      const n = parseInt(String(dstVal), 10);
      (next as any)[from] = isNaN(n) ? null : n;
    } else {
      (next as any)[from] = String(dstVal);
    }
  } else {
    (next as any)[from] = from === 'vendcat' ? null : '';
  }
  return next;
}

// ============================================================
// ROW WARNINGS (pure)
// ============================================================
function computeRowWarnings(row: CleanRow, categories: CategoryRow[]): string[] {
  const w: string[] = [];
  if (!row.name) w.push('missing name');
  if (!row.phone) w.push('missing phone');
  if (!row.location) w.push('missing location');
  const eff = effectiveVendcat(row, categories);
  if (eff.id === null) w.push('no vendcat (category unresolved)');
  return w;
}

// ============================================================
// COMPONENT
// ============================================================
const MassVendorUpload: React.FC = () => {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [pasteText, setPasteText] = useState('');
  const [rows, setRows] = useState<CleanRow[]>([]);
  const [parseError, setParseError] = useState('');
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);

  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [zoom, setZoom] = useState(1);

  const [totalBefore, setTotalBefore] = useState<number | null>(null);
  const [totalAfter, setTotalAfter] = useState<number | null>(null);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, success: 0, failed: 0 });
  const [report, setReport] = useState<{ success: number; failed: number; errors: string[] } | null>(null);

  const [dragging, setDragging] = useState<{ rowIdx: number; cell: CellKey } | null>(null);
  const [dragOverCell, setDragOverCell] = useState<{ rowIdx: number; cell: CellKey } | null>(null);
  const [selected, setSelected] = useState<{ rowIdx: number; cell: CellKey } | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  // ---- AUTH ----
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      setCurrentUser(user);
      if (!user) { navigate('/signin'); return; }
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists() && snap.data().role === 'admin') setIsAdmin(true);
        else navigate('/market');
      } catch { navigate('/market'); }
      finally { setAuthLoading(false); }
    });
    return () => unsub();
  }, [navigate]);

  // ---- CATEGORIES + BASELINE ----
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const { data } = await supabase.from('vendimgcat').select('id, name').order('name');
      if (data) setCategories(data as CategoryRow[]);
      const { count } = await supabase.from('vendor_profiles').select('*', { count: 'exact', head: true });
      setTotalBefore(count ?? 0);
    })();
  }, [isAdmin]);

  // ---- PARSE ON CHANGE ----
  useEffect(() => {
    if (!pasteText.trim()) { setRows([]); setParseError(''); setParseWarnings([]); return; }
    const res = parseWithMarkers(pasteText);
    // Seed warnings on the parsed rows immediately so "check these rows" reflects parse-time issues
    const seeded = res.rows.map(r => ({ ...r, warnings: computeRowWarnings(r, categories) }));
    setRows(seeded);
    setParseError(res.error || '');
    setParseWarnings(res.warnings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasteText, categories]);

  // ---- HELPERS ----
  const nn = (v: string | undefined | null): string | null => {
    const t = (v ?? '').trim();
    return t.length === 0 ? null : t;
  };
  const uuid = () => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  };
  const makeVendorId = (shopName: string, idx: number) => {
    const slug = shopName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    return `${currentUser?.uid}_${slug}_${Date.now()}_${idx}`;
  };

  const setCell = <K extends keyof CleanRow>(idx: number, key: K, value: CleanRow[K]) => {
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[idx], [key]: value, status: 'pending' as const, error: undefined };
      row.warnings = computeRowWarnings(row, categories);
      next[idx] = row;
      return next;
    });
  };

  const deleteRow = (idx: number) => setRows(prev => prev.filter((_, i) => i !== idx));

  // ---- DRAG ----
  const onCellDragStart = (e: React.DragEvent, rowIdx: number, cell: CellKey) => {
    const val = (rows[rowIdx] as any)[cell];
    if (val === '' || val === null || val === undefined) { e.preventDefault(); return; }
    setDragging({ rowIdx, cell });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(val));
  };
  const onCellDragOver = (e: React.DragEvent, rowIdx: number, cell: CellKey) => {
    if (!dragging || dragging.rowIdx !== rowIdx || dragging.cell === cell) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell({ rowIdx, cell });
  };
  const onCellDragLeave = () => setDragOverCell(null);
  const onCellDrop = (e: React.DragEvent, rowIdx: number, cell: CellKey) => {
    e.preventDefault();
    if (!dragging || dragging.rowIdx !== rowIdx) { setDragging(null); setDragOverCell(null); return; }
    setRows(prev => {
      const next = [...prev];
      const moved = moveCellValue(next[rowIdx], dragging.cell, cell);
      moved.warnings = computeRowWarnings(moved, categories);
      next[rowIdx] = moved;
      return next;
    });
    setDragging(null); setDragOverCell(null);
  };
  const onCellDragEnd = () => { setDragging(null); setDragOverCell(null); };

  const manualMove = (rowIdx: number, from: CellKey, to: CellKey) => {
    if (from === to) return;
    setRows(prev => {
      const next = [...prev];
      const moved = moveCellValue(next[rowIdx], from, to);
      moved.warnings = computeRowWarnings(moved, categories);
      next[rowIdx] = moved;
      return next;
    });
  };

  const clearCell = (rowIdx: number, cell: CellKey) => {
    setRows(prev => {
      const next = [...prev];
      (next[rowIdx] as any)[cell] = cell === 'vendcat' ? null : '';
      next[rowIdx].warnings = computeRowWarnings(next[rowIdx], categories);
      return next;
    });
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!selected) return;
    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); clearCell(selected.rowIdx, selected.cell); }
    if (e.key === 'Escape') setSelected(null);
  }, [selected]);

  // ---- UPLOAD ----
  const handleUpload = async () => {
    if (uploading || rows.length === 0) return;

    if (!currentUser?.uid) {
      setReport({ success: 0, failed: 0, errors: ['Not signed in — cannot upload.'] });
      return;
    }

    setUploading(true);
    setReport(null);
    setProgress({ done: 0, total: rows.length, success: 0, failed: 0 });

    const errors: string[] = [];
    let success = 0, failed = 0;

    const prepared: Array<{ idx: number; payload: any } | { idx: number; error: string }> = rows.map((r, idx) => {
      if (!r.name) return { idx, error: 'Missing shop name' };

      const eff = effectiveVendcat(r, categories);
      if (eff.id === null) return { idx, error: `Cannot resolve vendcat for "${r.categoryHint || 'no category'}"` };

      const now = new Date().toISOString();

      // Bio — row.bio + phone2 + overflow hashtags (comma-separated, no hashes)
      const bioParts: string[] = [];
      if (r.bio) bioParts.push(r.bio);
      if (r.phone2) bioParts.push(`Alt: ${r.phone2}`);
      const { kept, overflow } = splitHashtags(r.hashtags);
      if (overflow.length) bioParts.push(`More tags: ${overflow.join(', ')}`);
      const bio = bioParts.join(' • ');

      return {
        idx,
        payload: {
          id: uuid(),
          user_id: currentUser.uid,
          vendor_id: makeVendorId(r.name, idx),
          shop_name: r.name.trim(),
          bio: nn(bio),
          location: nn(r.location),
          contact_phone: nn(r.phone),
          business_email: nn(r.email),
          website: nn(r.website),
          instagram_handle: nn(r.instagram),
          facebook_page: nn(r.facebook),
          twitter_handle: nn(r.twitter),
          business_hours: null,
          return_policy: null,
          shipping_policy: null,
          profile_image: null,
          cover_image: null,
          followers_count: 0,
          likes_count: 0,
          is_active: true,
          onboarding_completed: true,
          created_at: now,
          updated_at: now,
          sales_count: 0,
          total_revenue: 0,
          logo_url: null,
          total_products: 0,
          total_sales: 0,
          average_rating: 0,
          rating_count: 0,
          rooms: [],
          room_count: 0,
          pending_balance: 0,
          available_balance: 0,
          virtual_account: null,
          balance: 0,
          total_earnings: 0,
          total_withdrawals: 0,
          last_withdrawal_date: null,
          vendcat: eff.id,
          hashtags: kept.length ? kept.map(t => `#${t}`).join('') : null,
        },
      };
    });

    const BATCH = 25;
    let processed = 0;

    for (let b = 0; b < prepared.length; b += BATCH) {
      const slice = prepared.slice(b, b + BATCH);
      const inserts: any[] = [];
      const insertIdxs: number[] = [];

      slice.forEach(item => {
        if ('error' in item) {
          failed++;
          errors.push(`Row ${item.idx + 1}: ${item.error}`);
          setRows(prev => { const n = [...prev]; n[item.idx] = { ...n[item.idx], status: 'error', error: item.error }; return n; });
        } else {
          inserts.push(item.payload);
          insertIdxs.push(item.idx);
        }
      });

      if (!inserts.length) {
        processed += slice.length;
        setProgress({ done: processed, total: prepared.length, success, failed });
        continue;
      }

      const { error } = await supabase.from('vendor_profiles').insert(inserts);

      if (!error) {
        insertIdxs.forEach(idx => {
          success++;
          setRows(prev => { const n = [...prev]; n[idx] = { ...n[idx], status: 'success' }; return n; });
        });
        processed += inserts.length;
        setProgress({ done: processed, total: prepared.length, success, failed });
      } else {
        // Retry individually to isolate bad rows
        for (let i = 0; i < inserts.length; i++) {
          const p = inserts[i];
          const idx = insertIdxs[i];
          const { error: e2 } = await supabase.from('vendor_profiles').insert([p]);
          if (e2) {
            failed++;
            errors.push(`Row ${idx + 1} (${p.shop_name}): ${e2.message}`);
            setRows(prev => { const n = [...prev]; n[idx] = { ...n[idx], status: 'error', error: e2.message }; return n; });
          } else {
            success++;
            setRows(prev => { const n = [...prev]; n[idx] = { ...n[idx], status: 'success' }; return n; });
          }
          processed++;
          setProgress({ done: processed, total: prepared.length, success, failed });
        }
      }
    }

    const { count } = await supabase.from('vendor_profiles').select('*', { count: 'exact', head: true });
    setTotalAfter(count ?? 0);
    setReport({ success, failed, errors });
    setUploading(false);
  };

  // ---- ZOOM ----
  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    setZoom(z => Math.min(2, Math.max(0.5, +(z + (e.deltaY < 0 ? 0.1 : -0.1)).toFixed(2))));
  };

  // ---- LIVE STATS ----
  const stats = useMemo(() => {
    const issueRows = rows
      .map((r, i) => ({ row: r, idx: i }))
      .filter(({ row }) => row.warnings.length > 0 || row.extras.length > 0);
    return {
      total: rows.length,
      success: rows.filter(r => r.status === 'success').length,
      failed: rows.filter(r => r.status === 'error').length,
      issueRows,
    };
  }, [rows]);

  // ---- GUARDS ----
  if (authLoading) {
    return (<div className="mvu-loading"><div className="mvu-spinner" /><p>Checking access…</p></div>);
  }
  if (!isAdmin) return null;

  // ---- COLUMNS ----
  const columns: { key: CellKey; label: string; note: string; type?: 'text' | 'number' | 'category' }[] = [
    { key: 'name', label: 'Shop name', note: 'shop_name *' },
    { key: 'phone', label: 'Phone', note: 'contact_phone *' },
    { key: 'location', label: 'Location', note: 'location *' },
    { key: 'bio', label: 'Bio', note: 'bio (extra info)' },
    { key: 'email', label: 'Email', note: 'business_email' },
    { key: 'website', label: 'Website', note: 'website' },
    { key: 'instagram', label: 'Instagram', note: 'instagram_handle' },
    { key: 'facebook', label: 'Facebook', note: 'facebook_page' },
    { key: 'twitter', label: 'Twitter', note: 'twitter_handle' },
    { key: 'vendcat', label: 'Vendcat', note: 'vendcat (int)', type: 'number' },
    { key: 'categoryHint', label: 'Category hint', note: '→ vendcat if empty', type: 'category' },
    { key: 'hashtags', label: 'Hashtags', note: `first ${MAX_HASHTAGS} uploaded` },
  ];

  // ---- RENDER ----
  return (
    <div className="mvu-root" onKeyDown={handleKeyDown} tabIndex={-1}>
      <header className="mvu-header">
        <div className="mvu-header-left">
          <button className="mvu-icon-btn" onClick={() => navigate('/admin')} title="Back"><ArrowLeft size={18} /></button>
          <div>
            <h1>Mass Vendor Upload</h1>
            <p>
              Wrap each row in <code>@start</code> … <code>@end</code>. Cells are auto-classified by content
              and self-healed back from bio. <strong>Drag any cell into another cell of the same row</strong> to move values.
              Only the first <strong>{MAX_HASHTAGS}</strong> hashtags upload; the rest go to bio.
            </p>
          </div>
        </div>
        <div className="mvu-header-right">
          <div className="mvu-total-pill"><Store size={14} /><span>Before:</span><strong>{totalBefore ?? '—'}</strong></div>
          <div className="mvu-total-pill"><Store size={14} /><span>After:</span><strong>{totalAfter ?? '—'}</strong></div>
        </div>
      </header>

      <section className="mvu-panel">
        <div className="mvu-panel-header">
          <div className="mvu-panel-title"><ClipboardPaste size={16} /><span>Step 1 — Paste your rows</span></div>
          <div className="mvu-panel-actions">
            <button
              className="mvu-btn ghost"
              onClick={() => { setPasteText(''); setRows([]); setReport(null); setTotalAfter(null); setParseError(''); setParseWarnings([]); }}
              disabled={uploading}
            >
              <Trash2 size={14} /> Clear
            </button>
          </div>
        </div>
        <div className="mvu-help">
          <Info size={14} />
          <div>
            <strong>Format:</strong> every row wrapped in <code>@start</code> … <code>@end</code>.
            Phones like <code>0803… / 0818…</code>, <code>+234…</code>, <code>0803 839 3389</code> are normalized automatically.
            <pre className="mvu-help-code">{`@start\tChinny's Place\t08038393389 / 08189979009\tNo C/71 Think Home Estate, Awka\t@chinnysplace\thttps://chinnysplace.com\t1\tFood & Nutrition\t#restaurant #igbofood@end`}</pre>
          </div>
        </div>
        <textarea
          className="mvu-paste-area"
          placeholder={`Paste @start … @end blocks here.\n\n@start\tName\tPhone\tLocation\tSocials\tSources\tEmail\tvendcat\tHashtags@end`}
          value={pasteText}
          onChange={e => setPasteText(e.target.value)}
          spellCheck={false}
          disabled={uploading}
        />
        {parseError && (<div className="mvu-alert error"><AlertCircle size={16} /><div style={{ whiteSpace: 'pre-wrap' }}>{parseError}</div></div>)}
        {!parseError && rows.length > 0 && (
          <div className="mvu-alert ok">
            <CheckCircle size={16} />
            <span>Parsed <strong>{rows.length}</strong> row{rows.length !== 1 ? 's' : ''}.{stats.issueRows.length > 0 && ` ${stats.issueRows.length} with issues.`}</span>
          </div>
        )}
        {parseWarnings.length > 0 && (
          <div className="mvu-alert warn">
            <AlertCircle size={16} />
            <ul style={{ margin: 0, paddingLeft: 18 }}>{parseWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}
      </section>

      {rows.length > 0 && (
        <section className="mvu-panel">
          <div className="mvu-panel-header">
            <div className="mvu-panel-title">
              <Table2 size={16} /><span>Step 2 — Review, drag, edit</span>
              <span className="mvu-badge">{rows.length} rows</span>
              {stats.success > 0 && <span className="mvu-badge success">{stats.success} uploaded</span>}
              {stats.failed > 0 && <span className="mvu-badge error">{stats.failed} errors</span>}
            </div>
            <div className="mvu-panel-actions">
              <button className="mvu-icon-btn" onClick={() => setZoom(z => Math.min(2, +(z + 0.1).toFixed(2)))} title="Zoom in"><ZoomIn size={16} /></button>
              <span className="mvu-zoom-label">{Math.round(zoom * 100)}%</span>
              <button className="mvu-icon-btn" onClick={() => setZoom(z => Math.max(0.5, +(z - 0.1).toFixed(2)))} title="Zoom out"><ZoomOut size={16} /></button>
              <button className="mvu-icon-btn" onClick={() => setZoom(1)} title="Reset"><Maximize2 size={16} /></button>
            </div>
          </div>

          <div className="mvu-drag-hint">
            <Move size={12} />
            Drag a filled cell onto another cell of the <strong>same row</strong> to move the value.
            Select a cell and press <kbd>Delete</kbd> to clear it.
          </div>

          <div className="mvu-table-wrap" onWheel={onWheel}>
            <div className="mvu-table-scroll" ref={scrollRef}>
              <table className="mvu-table" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
                <thead>
                  <tr>
                    <th className="mvu-th-status">#</th>
                    {columns.map(col => (
                      <th key={col.key} className={col.key === 'bio' ? 'mvu-th-bio' : ''}>
                        <div className="mvu-th-inner">
                          <span>{col.label}</span>
                          <span className="mvu-th-note">{col.note}</span>
                        </div>
                      </th>
                    ))}
                    <th className="mvu-th-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, rowIdx) => {
                    const eff = effectiveVendcat(r, categories);
                    const rowHasIssue = r.warnings.length > 0 || r.extras.length > 0 || eff.id === null;
                    return (
                      <tr key={rowIdx} className={`mvu-row status-${r.status} ${rowHasIssue ? 'has-issue' : ''}`}>
                        <td className="mvu-td-status">
                          <div className={`mvu-status-dot ${r.status}`} title={r.error || r.warnings.join(', ')}>
                            {r.status === 'success' && <CheckCircle size={14} />}
                            {r.status === 'error' && <XCircle size={14} />}
                            {r.status === 'uploading' && <Loader size={14} className="mvu-spin" />}
                            {r.status === 'pending' && <span>{rowIdx + 1}</span>}
                          </div>
                        </td>

                        {columns.map(col => {
                          const value = (r as any)[col.key];
                          const isEmpty = value === '' || value === null || value === undefined;
                          const isDragSrc = dragging?.rowIdx === rowIdx && dragging.cell === col.key;
                          const isDropTgt = dragOverCell?.rowIdx === rowIdx && dragOverCell.cell === col.key;
                          const isSel = selected?.rowIdx === rowIdx && selected.cell === col.key;
                          const required = col.key === 'name' || col.key === 'phone' || col.key === 'location';
                          const invalid = required && isEmpty;

                          const cellCls = [
                            'mvu-cell',
                            isEmpty && 'mvu-cell-empty',
                            isDragSrc && 'mvu-cell-dragging',
                            isDropTgt && 'mvu-cell-drop',
                            isSel && 'mvu-cell-selected',
                            invalid && 'mvu-cell-invalid',
                          ].filter(Boolean).join(' ');

                          const dragProps = {
                            draggable: !isEmpty && !uploading,
                            onDragStart: (e: React.DragEvent) => onCellDragStart(e, rowIdx, col.key),
                            onDragOver: (e: React.DragEvent) => onCellDragOver(e, rowIdx, col.key),
                            onDragLeave: onCellDragLeave,
                            onDrop: (e: React.DragEvent) => onCellDrop(e, rowIdx, col.key),
                            onDragEnd: onCellDragEnd,
                          };
                          const selectProps = {
                            onClick: (e: React.MouseEvent) => { e.stopPropagation(); setSelected({ rowIdx, cell: col.key }); },
                          };

                          return (
                            <td key={col.key} className={cellCls} {...dragProps} {...selectProps}>
                              <div className="mvu-cell-inner">
                                {!isEmpty && (
                                  <span className="mvu-grip-wrap" title="Drag to move">
                                    <GripVertical size={10} className="mvu-grip" />
                                  </span>
                                )}

                                {col.type === 'category' ? (
                                  <select
                                    className="mvu-input"
                                    value={(r.categoryHint as string) || ''}
                                    onChange={e => setCell(rowIdx, 'categoryHint', e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    disabled={uploading}
                                  >
                                    <option value="">—</option>
                                    {categories.map(c => (<option key={c.id} value={c.name}>{c.name} (id {c.id})</option>))}
                                    {r.categoryHint && !categories.some(c => c.name === r.categoryHint) && (
                                      <option value={r.categoryHint}>{r.categoryHint}</option>
                                    )}
                                  </select>
                                ) : col.type === 'number' ? (
                                  <input
                                    className={`mvu-input mvu-input-narrow ${eff.auto && !r.vendcat ? 'mvu-input-auto' : ''}`}
                                    value={eff.id ?? ''}
                                    onChange={e => setCell(rowIdx, 'vendcat', e.target.value ? Number(e.target.value) : null)}
                                    onClick={e => e.stopPropagation()}
                                    title={eff.auto && !r.vendcat ? 'Auto-suggested from category — will be uploaded' : ''}
                                    disabled={uploading}
                                  />
                                ) : (
                                  <input
                                    className="mvu-input"
                                    value={(value ?? '') as string}
                                    onChange={e => setCell(rowIdx, col.key as any, e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    disabled={uploading}
                                  />
                                )}

                                {!isEmpty && (
                                  <select
                                    className="mvu-cell-move"
                                    value=""
                                    onChange={e => { if (e.target.value) manualMove(rowIdx, col.key, e.target.value as CellKey); }}
                                    title="Move to…"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <option value="">Move to…</option>
                                    {columns.filter(c => c.key !== col.key).map(c => (
                                      <option key={c.key} value={c.key}>{c.label}</option>
                                    ))}
                                  </select>
                                )}

                                {col.type === 'number' && eff.auto && !r.vendcat && (
                                  <div className="mvu-resolve-hint"><Wand2 size={10} />from category</div>
                                )}
                              </div>
                            </td>
                          );
                        })}

                        <td className="mvu-td-actions">
                          <button className="mvu-icon-btn small danger" onClick={() => deleteRow(rowIdx)} disabled={uploading} title="Remove row"><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {stats.issueRows.length > 0 && (
            <div className="mvu-alert warn" style={{ marginTop: 12 }}>
              <AlertCircle size={16} />
              <div>
                <strong>{stats.issueRows.length} row{stats.issueRows.length !== 1 ? 's' : ''} need attention:</strong>
                <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                  {stats.issueRows.map(({ row, idx }) => {
                    const eff = effectiveVendcat(row, categories);
                    const issues: string[] = [...row.warnings];
                    if (eff.id === null && !row.vendcat) issues.push('no vendcat');
                    if (row.extras.length) issues.push(`extra: ${row.extras.join(', ')}`);
                    if (!issues.length) return null;
                    return (
                      <li key={idx}>
                        <strong>{row.name || `Row ${idx + 1}`}:</strong> {issues.join(' · ')}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </section>
      )}

      {rows.length > 0 && (
        <section className="mvu-upload-bar">
          <div className="mvu-upload-left">
            {uploading ? (
              <><Loader size={16} className="mvu-spin" /><span>Uploading {progress.done} / {progress.total}…</span>
                <div className="mvu-progress"><div className="mvu-progress-fill" style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }} /></div>
              </>
            ) : report ? (
              <><CheckCircle size={16} className="ok-icon" /><span><strong>{report.success}</strong> uploaded · <strong>{report.failed}</strong> failed</span></>
            ) : (
              <><Upload size={16} /><span>Ready to upload <strong>{rows.length}</strong> row{rows.length !== 1 ? 's' : ''}</span></>
            )}
          </div>
          <div className="mvu-upload-right">
            <button className="mvu-btn primary" onClick={handleUpload} disabled={uploading || rows.length === 0}>
              {uploading ? <><Loader size={14} className="mvu-spin" /> Uploading…</> : <><Upload size={14} /> Upload to vendor_profiles</>}
            </button>
            <button className="mvu-btn ghost" onClick={() => { setReport(null); setTotalAfter(null); }} disabled={uploading}><RefreshCw size={14} /> Reset</button>
          </div>
        </section>
      )}

      {report && (
        <div className="mvu-alert ok" style={{ marginTop: 12 }}>
          <CheckCircle size={16} />
          <div>
            <strong>Done.</strong> {report.success} succeeded, {report.failed} failed.
            {report.errors.length > 0 && (
              <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>{report.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MassVendorUpload;